import Seo from '@/components/Seo';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Minus, Plus, Trash2, ShoppingBag, Loader2 } from 'lucide-react';
import { getCart, updateCartItem, removeFromCart, clearCart, createCheckout } from '@/lib/api';
import { toast } from 'sonner';

const emptyForm = {
  email: '',
  name: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
};

const CartPage = () => {
  const [cart, setCart] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [updatingItems, setUpdatingItems] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const navigate = useNavigate();

  const fetchCart = async () => {
    try {
      const data = await getCart();
      setCart(data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  const handleUpdateQuantity = async (bookId, newQuantity, format) => {
    const key = `${bookId}-${format}`;
    setUpdatingItems({ ...updatingItems, [key]: true });
    try {
      await updateCartItem(bookId, newQuantity, format);
      await fetchCart();
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      toast.error('Failed to update quantity');
    } finally {
      setUpdatingItems({ ...updatingItems, [key]: false });
    }
  };

  const handleRemoveItem = async (bookId, format) => {
    try {
      await removeFromCart(bookId, format);
      await fetchCart();
      window.dispatchEvent(new Event('cartUpdated'));
      toast.success('Item removed');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleClearCart = async () => {
    try {
      await clearCart();
      await fetchCart();
      window.dispatchEvent(new Event('cartUpdated'));
      toast.success('Cart cleared');
    } catch (error) {
      toast.error('Failed to clear cart');
    }
  };

  const cartItems = cart?.populated_items || [];
  const subtotal = cartItems.reduce((sum, item) => sum + (item.book?.price || 0) * item.quantity, 0);
  const isEmpty = cartItems.length === 0;
  const hasPhysical = cartItems.some((item) => item.format === 'physical');

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!form.name.trim()) {
      toast.error('Please enter your full name');
      return false;
    }
    if (!form.phone.trim()) {
      toast.error('Please enter your phone number');
      return false;
    }
    if (hasPhysical) {
      if (!form.address_line1.trim() || !form.city.trim() || !form.state.trim() || !form.postal_code.trim() || !form.country.trim()) {
        toast.error('Please complete the shipping address for physical books');
        return false;
      }
    }
    return true;
  };

  const handleCheckout = async () => {
    if (!showForm) {
      setShowForm(true);
      setTimeout(() => {
        document.getElementById('checkout-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    if (!validateForm()) return;

    setIsCheckingOut(true);
    try {
      const shippingInfo = {
        name: form.name,
        phone: form.phone,
        ...(hasPhysical && {
          address_line1: form.address_line1,
          address_line2: form.address_line2,
          city: form.city,
          state: form.state,
          postal_code: form.postal_code,
          country: form.country,
        }),
      };
      const result = await createCheckout(form.email, shippingInfo);
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      toast.error('Checkout failed. Please try again.');
      setIsCheckingOut(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="cart-page">
      <Seo title="Your Cart" noindex />
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <Link
            to="/books"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-8"
            data-testid="continue-shopping"
          >
            <ArrowLeft size={16} />
            <span className="text-sm uppercase tracking-widest">Continue Shopping</span>
          </Link>
          <h1 className="text-3xl lg:text-4xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
            Your Cart
          </h1>
        </motion.div>

        {isEmpty ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-24"
          >
            <ShoppingBag size={48} className="mx-auto text-white/20 mb-6" />
            <p className="text-white/60 text-lg mb-6">Your cart is empty</p>
            <Link
              to="/books"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="browse-books-btn"
            >
              Browse the Bookstore
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cartItems.map((item, index) => (
                <motion.div
                  key={`${item.book_id}-${item.format}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="card-surface p-6 flex gap-6"
                  data-testid={`cart-item-${item.book_id}`}
                >
                  {/* Book Cover */}
                  <Link to={`/books/${item.book_id}`} className="flex-shrink-0">
                    <img
                      src={item.book?.cover_image}
                      alt={item.book?.title}
                      className="w-20 aspect-[2/3] object-cover"
                    />
                  </Link>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/books/${item.book_id}`}
                      className="hover:text-gold transition-colors"
                    >
                      <h3 className="font-medium mb-1 truncate" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {item.book?.title}
                      </h3>
                    </Link>
                    <p className="text-white/50 text-sm mb-2">{item.book?.author}</p>
                    <p className="mono text-xs text-white/40 uppercase">
                      {item.format === 'digital' ? 'eBook' : 'Paperback'}
                    </p>

                    <div className="flex items-center gap-4 mt-4">
                      {/* Quantity Controls */}
                      <div className="flex items-center border border-white/10">
                        <button
                          onClick={() => handleUpdateQuantity(item.book_id, item.quantity - 1, item.format)}
                          disabled={updatingItems[`${item.book_id}-${item.format}`]}
                          className="p-2 text-white/50 hover:text-white transition-colors disabled:opacity-50"
                          data-testid={`decrease-qty-${item.book_id}`}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-4 mono text-sm" data-testid={`qty-${item.book_id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleUpdateQuantity(item.book_id, item.quantity + 1, item.format)}
                          disabled={updatingItems[`${item.book_id}-${item.format}`]}
                          className="p-2 text-white/50 hover:text-white transition-colors disabled:opacity-50"
                          data-testid={`increase-qty-${item.book_id}`}
                        >
                          <Plus size={14} />
                        </button>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => handleRemoveItem(item.book_id, item.format)}
                        className="p-2 text-white/30 hover:text-red-500 transition-colors"
                        data-testid={`remove-item-${item.book_id}`}
                        aria-label="Remove item"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-right">
                    <p className="mono text-gold" data-testid={`item-price-${item.book_id}`}>
                      ${(item.book?.price * item.quantity).toFixed(2)}
                    </p>
                    {item.quantity > 1 && (
                      <p className="mono text-xs text-white/40 mt-1">
                        ${item.book?.price?.toFixed(2)} each
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}

              {/* Clear Cart */}
              <button
                onClick={handleClearCart}
                className="text-white/30 hover:text-white/60 transition-colors text-sm"
                data-testid="clear-cart-btn"
              >
                Clear Cart
              </button>
            </div>

            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className="card-surface p-8 sticky top-32">
                <h2 className="text-xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Order Summary
                </h2>

                <div className="space-y-4 mb-6 pb-6 border-b border-white/10">
                  <div className="flex justify-between text-white/60">
                    <span>Subtotal</span>
                    <span className="mono">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-white/60">
                    <span>Shipping</span>
                    <span className="mono">Calculated at checkout</span>
                  </div>
                </div>

                <div className="flex justify-between text-lg font-medium mb-8">
                  <span>Total</span>
                  <span className="mono text-gold" data-testid="cart-total">${subtotal.toFixed(2)}</span>
                </div>

                <AnimatePresence>
                  {showForm && (
                    <motion.div
                      id="checkout-form"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4 }}
                      className="overflow-hidden mb-8"
                      data-testid="checkout-form"
                    >
                      <div className="space-y-4 pb-6 border-b border-white/10">
                        <p className="text-sm uppercase tracking-widest text-white/50 mb-2">
                          {hasPhysical ? 'Contact & Shipping' : 'Contact Details'}
                        </p>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleFormChange}
                          placeholder="Email address *"
                          className="checkout-input"
                          data-testid="checkout-email-input"
                        />
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleFormChange}
                          placeholder="Full name *"
                          className="checkout-input"
                          data-testid="checkout-name-input"
                        />
                        <input
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={handleFormChange}
                          placeholder="Phone number *"
                          className="checkout-input"
                          data-testid="checkout-phone-input"
                        />
                        {hasPhysical && (
                          <>
                            <input
                              type="text"
                              name="address_line1"
                              value={form.address_line1}
                              onChange={handleFormChange}
                              placeholder="Street address *"
                              className="checkout-input"
                              data-testid="checkout-address1-input"
                            />
                            <input
                              type="text"
                              name="address_line2"
                              value={form.address_line2}
                              onChange={handleFormChange}
                              placeholder="Apt, suite, etc. (optional)"
                              className="checkout-input"
                              data-testid="checkout-address2-input"
                            />
                            <div className="grid grid-cols-2 gap-4">
                              <input
                                type="text"
                                name="city"
                                value={form.city}
                                onChange={handleFormChange}
                                placeholder="City *"
                                className="checkout-input"
                                data-testid="checkout-city-input"
                              />
                              <input
                                type="text"
                                name="state"
                                value={form.state}
                                onChange={handleFormChange}
                                placeholder="State / Province *"
                                className="checkout-input"
                                data-testid="checkout-state-input"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <input
                                type="text"
                                name="postal_code"
                                value={form.postal_code}
                                onChange={handleFormChange}
                                placeholder="ZIP / Postal code *"
                                className="checkout-input"
                                data-testid="checkout-postal-input"
                              />
                              <input
                                type="text"
                                name="country"
                                value={form.country}
                                onChange={handleFormChange}
                                placeholder="Country *"
                                className="checkout-input"
                                data-testid="checkout-country-input"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <button
                  onClick={handleCheckout}
                  disabled={isCheckingOut}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="checkout-btn"
                >
                  {isCheckingOut ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Processing...
                    </>
                  ) : showForm ? (
                    <>Pay ${subtotal.toFixed(2)}</>
                  ) : (
                    <>Proceed to Checkout</>
                  )}
                </button>

                <p className="text-white/40 text-xs text-center mt-4">
                  Secure checkout powered by Stripe
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
